import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import {
  AdminCreateOrganizationToolSetAssignmentPayload,
  AdminCreateOrganizationToolSetAssignmentResponse,
  AdminListOrganizationToolSetAssignmentsResponse,
  AdminListToolSetsResponse,
  AdminShowToolSetResponse,
  AdminToolSetsListQueryParams,
  AdminUpdateToolSetPayload,
  AdminUpdateToolSetResponse,
  CreateToolSetExclusionPayload,
  CreateToolSetExclusionResponse,
  ListToolSetExclusionsResponse,
  ToolSet,
  ToolSetsListParams,
  UserListToolSetsResponse,
  UserShowToolSetResponse,
} from "./types";

// Export types
export * from "./types";

const ENDPOINTS = {
  ADMIN: {
    LIST: "/api/v2/admin/tool-sets",
    SHOW: "/api/v2/admin/tool-sets/:tool_set_id",
    UPDATE: "/api/v2/admin/tool-sets/:tool_set_id",
    LIST_ORG_ASSIGNMENTS:
      "/api/v2/admin/organizations/:organization_id/tool-set-assignments",
    CREATE_ORG_ASSIGNMENT:
      "/api/v2/admin/organizations/:organization_id/tool-set-assignments",
    DELETE_ORG_ASSIGNMENT:
      "/api/v2/admin/organizations/:organization_id/tool-set-assignments/:tool_set_id",
  },
  ORG: {
    LIST_EXCLUSIONS: "/api/v2/org/:organization_id/tool-set-exclusions",
    CREATE_EXCLUSION: "/api/v2/org/:organization_id/tool-set-exclusions",
    DELETE_EXCLUSION:
      "/api/v2/org/:organization_id/tool-set-exclusions/:tool_set_exclusion_id",
    LIST_USER_TOOL_SETS: "/api/v2/org/:organization_id/user/:user_id/tool-sets",
    SHOW_USER_TOOL_SET:
      "/api/v2/org/:organization_id/user/:user_id/tool-sets/:tool_set_id",
  },
};

export class ToolSetsAPI {
  private client: CohesiveAPIClient;

  constructor(client: CohesiveAPIClient) {
    this.client = client;
  }

  // Admin endpoints

  /**
   * List tool sets (Admin) with optional filters, pagination, and global stats.
   */
  async adminList(
    params?: AdminToolSetsListQueryParams,
    config?: AxiosRequestConfig,
  ): Promise<AdminListToolSetsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set("search", params.search);
    if (params?.type) searchParams.set("type", params.type);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.access_tier)
      searchParams.set("access_tier", params.access_tier);
    if (params?.limit != null) searchParams.set("limit", String(params.limit));
    if (params?.offset != null)
      searchParams.set("offset", String(params.offset));

    const qs = searchParams.toString();
    const url = `${ENDPOINTS.ADMIN.LIST}${qs ? `?${qs}` : ""}`;
    const response = await this.client.axios.get<AdminListToolSetsResponse>(
      url,
      config,
    );
    return response.data;
  }

  /**
   * Get a specific tool set (Admin)
   */
  async adminShow(
    toolSetId: string,
    config?: AxiosRequestConfig,
  ): Promise<ToolSet> {
    const response = await this.client.axios.get<AdminShowToolSetResponse>(
      route(ENDPOINTS.ADMIN.SHOW, { tool_set_id: toolSetId }),
      config,
    );
    return response.data.data.tool_set;
  }

  /**
   * Update a tool set status (Admin)
   */
  async adminUpdate(
    toolSetId: string,
    payload: AdminUpdateToolSetPayload,
    config?: AxiosRequestConfig,
  ): Promise<ToolSet> {
    const response = await this.client.axios.put<AdminUpdateToolSetResponse>(
      route(ENDPOINTS.ADMIN.UPDATE, { tool_set_id: toolSetId }),
      payload,
      config,
    );
    return response.data.data.tool_set;
  }

  /**
   * List organization tool set assignments (Admin)
   */
  async adminListOrganizationAssignments(
    organizationId: string,
    config?: AxiosRequestConfig,
  ): Promise<ToolSet[]> {
    const response =
      await this.client.axios.get<AdminListOrganizationToolSetAssignmentsResponse>(
        route(ENDPOINTS.ADMIN.LIST_ORG_ASSIGNMENTS, {
          organization_id: organizationId,
        }),
        config,
      );
    return response.data.data.tool_sets;
  }

  /**
   * Create organization tool set assignment (Admin)
   */
  async adminCreateOrganizationAssignment(
    organizationId: string,
    payload: AdminCreateOrganizationToolSetAssignmentPayload,
    config?: AxiosRequestConfig,
  ): Promise<
    AdminCreateOrganizationToolSetAssignmentResponse["data"]["organization_tool_set_assignment"]
  > {
    const response =
      await this.client.axios.post<AdminCreateOrganizationToolSetAssignmentResponse>(
        route(ENDPOINTS.ADMIN.CREATE_ORG_ASSIGNMENT, {
          organization_id: organizationId,
        }),
        payload,
        config,
      );
    return response.data.data.organization_tool_set_assignment;
  }

  /**
   * Delete organization tool set assignment (Admin)
   */
  async adminDeleteOrganizationAssignment(
    organizationId: string,
    toolSetId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    await this.client.axios.delete(
      route(ENDPOINTS.ADMIN.DELETE_ORG_ASSIGNMENT, {
        organization_id: organizationId,
        tool_set_id: toolSetId,
      }),
      config,
    );
  }

  // Organization endpoints

  /**
   * List tool set exclusions for organization
   */
  async listExclusions(config?: AxiosRequestConfig): Promise<ToolSet[]> {
    const orgId = await this.client.getOrgID();
    const response = await this.client.axios.get<ListToolSetExclusionsResponse>(
      route(ENDPOINTS.ORG.LIST_EXCLUSIONS, { organization_id: orgId || "" }),
      config,
    );
    return response.data.data.tool_sets;
  }

  /**
   * Create tool set exclusion for organization
   */
  async createExclusion(
    payload: CreateToolSetExclusionPayload,
    config?: AxiosRequestConfig,
  ): Promise<
    CreateToolSetExclusionResponse["data"]["organization_tool_set_exclusion"]
  > {
    const orgId = await this.client.getOrgID();
    const response =
      await this.client.axios.post<CreateToolSetExclusionResponse>(
        route(ENDPOINTS.ORG.CREATE_EXCLUSION, { organization_id: orgId || "" }),
        payload,
        config,
      );
    return response.data.data.organization_tool_set_exclusion;
  }

  /**
   * Delete tool set exclusion for organization
   */
  async orgDeleteExclusion(
    exclusionId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const orgId = await this.client.getOrgID();
    await this.client.axios.delete(
      route(ENDPOINTS.ORG.DELETE_EXCLUSION, {
        organization_id: orgId || "",
        tool_set_exclusion_id: exclusionId,
      }),
      config,
    );
  }

  /**
   * List tool sets available to a user in an organization
   */
  async userList(
    params?: ToolSetsListParams,
    config?: AxiosRequestConfig,
  ): Promise<UserListToolSetsResponse> {
    const orgId = await this.client.getOrgID();
    const uid = await this.client.getUserID();

    const requestConfig = {
      ...config,
      params: {
        ...config?.params,
        ...params,
      },
    };

    const response = await this.client.axios.get<UserListToolSetsResponse>(
      route(ENDPOINTS.ORG.LIST_USER_TOOL_SETS, {
        organization_id: orgId || "",
        user_id: uid || "",
      }),
      requestConfig,
    );
    return response.data;
  }

  /**
   * Get a specific tool set for a user in an organization
   */
  async userShow(
    toolSetId: string,
    config?: AxiosRequestConfig,
  ): Promise<UserShowToolSetResponse["data"]["tool_set"]> {
    const orgId = await this.client.getOrgID();
    const uid = await this.client.getUserID();

    const response = await this.client.axios.get<UserShowToolSetResponse>(
      route(ENDPOINTS.ORG.SHOW_USER_TOOL_SET, {
        organization_id: orgId || "",
        user_id: uid || "",
        tool_set_id: toolSetId,
      }),
      config,
    );
    return response.data.data.tool_set;
  }
}
